import { TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';
import { FC_BaseProjectSettings_DTO, ProjectTextSettingKey, TextFieldSettings } from '../project.dto';
import { SpreadTypeEnum } from '../spread.dto';
import { applySettingsToModel } from './apply-settings-to-model.util';

type PhotoBookModel = Record<string, unknown> | object | null | undefined;

/**
 * Project-owned text kinds a vignette spread can carry — the same closed set the cover gate owns
 * (`COVER_PROJECT_TEXT_TYPES`). Vignette design texts (TITLE/SUBTITLE*, «НАШ ВЫПУСК») are authored
 * inside the template and are deliberately absent: they are not album data.
 */
export const VIGNETTE_PROJECT_TEXT_TYPES: readonly string[] = [
  TextTypeEnum.SCHOOL,
  TextTypeEnum.CLASS,
  TextTypeEnum.CITY,
  TextTypeEnum.GRADUATION_YEAR,
];

const VIGNETTE_SPREADS_ONLY: ReadonlySet<SpreadTypeEnum> = new Set([SpreadTypeEnum.VIGNETTE]);

/**
 * A vignette line that IS album data but was never typed as such. Lumir's own vignette library
 * carries no `class` slot at all — not one box in it uses TextTypeEnum.CLASS. The PSD author typed
 * that line with the vignette's generic design type and left the WORD that belongs there («КЛАСС»)
 * standing as its text, so the album's class never had anywhere to land.
 *
 * The slot is identified by the PAIR (textType, untouched design word) — «ВЫПУСК» is a caption when
 * it carries TITLE (printed above a real GRADUATION_YEAR box; filling it would print the year twice)
 * and the year line when it carries SUBTITLE2/3 with no year box around. The type alone does not
 * decide it, though: the library also ships stamps where SUBTITLE2/3 «ВЫПУСК» sit NEXT TO a real
 * GRADUATION_YEAR box (stud/vignette3-class, vign/vignette24) and stacked SUBTITLE2+SUBTITLE3
 * «ВЫПУСК» pairs with no year box at all (vign/vignette3/7/11/12). The year must land exactly once,
 * so the fill is resolved per collage — see `collageCaptionContext`.
 *
 * «НАШ» (TITLE) is deliberately absent — it is a design word with no album value behind it.
 */
interface VignetteCaptionSlot {
  /** The design word the template ships. A box saying anything else has been authored — never touched. */
  readonly sample: string;
  readonly settingsKey: ProjectTextSettingKey;
}

const VIGNETTE_CAPTION_SLOTS: ReadonlyMap<string, VignetteCaptionSlot> = new Map<string, VignetteCaptionSlot>([
  [VignetteTextTypeEnum.SUBTITLE, { sample: 'КЛАСС', settingsKey: TextTypeEnum.CLASS as ProjectTextSettingKey }],
  [VignetteTextTypeEnum.SUBTITLE2, { sample: 'ВЫПУСК', settingsKey: TextTypeEnum.GRADUATION_YEAR as ProjectTextSettingKey }],
  [VignetteTextTypeEnum.SUBTITLE3, { sample: 'ВЫПУСК', settingsKey: TextTypeEnum.GRADUATION_YEAR as ProjectTextSettingKey }],
]);

/**
 * FKS-10460: fills school/class/city/graduationYear slots on VIGNETTE spreads from the album's own
 * `settings.textSettings`. Vignette templates ship those slots with their design caption («КЛАСС»,
 * «2025»); nothing ever replaced them, so every album printed the template's words.
 *
 * Two passes, because the vignette holds album data under two different disguises: the typed slots
 * (`applySettingsToModel`, mostly GRADUATION_YEAR) and the design-caption slots that were never
 * given an album-data textType at all (`VIGNETTE_CAPTION_SLOTS` — the «КЛАСС»/«ВЫПУСК» lines).
 *
 * Text only — a project-level fontSize/color was authored against a cover slot and would rewrite the
 * vignette's typography. Person slots stay untouched: `applyPersonsToVignetteModel` owns those.
 * A settings key without a real value is skipped, so an unfilled album keeps its design caption
 * instead of going blank.
 *
 * Reference-preserving — an already-converged model comes back identical, which is what keeps a
 * plain reload from looking like an edit to the editor's autosave. The caption pass converges for
 * the same reason it never overwrites a person's own words: a filled line no longer reads «КЛАСС».
 */
export function applyProjectTextsToVignettes<T extends PhotoBookModel>(
  model: T,
  textSettings: FC_BaseProjectSettings_DTO['textSettings'] | null | undefined,
): T {
  if (!model) return model;

  const filled: Partial<Record<ProjectTextSettingKey, TextFieldSettings>> = {};
  for (const textType of VIGNETTE_PROJECT_TEXT_TYPES) {
    const key = textType as ProjectTextSettingKey;
    const value = textSettings?.[key]?.text;
    if (typeof value === 'string' && value.trim() !== '') filled[key] = { text: value };
  }

  // No values still runs the caption pass: a year line without a year must stop repeating «ВЫПУСК».
  const typed =
    Object.keys(filled).length === 0
      ? model
      : applySettingsToModel(model, { textSettings: filled } as Partial<FC_BaseProjectSettings_DTO>, {
          textOnly: true,
          spreadTypes: VIGNETTE_SPREADS_ONLY,
        });

  return applyCaptionSlots(typed, filled);
}

function applyCaptionSlots<T extends PhotoBookModel>(
  model: T,
  filled: Partial<Record<ProjectTextSettingKey, TextFieldSettings>>,
): T {
  const modelRecord = model as Record<string, unknown>;
  const spreads = modelRecord['spreads'];
  if (!Array.isArray(spreads)) return model;

  let changed = false;
  const next = (spreads as Record<string, unknown>[]).map(spread => {
    if (spread['spread_type'] !== SpreadTypeEnum.VIGNETTE || !Array.isArray(spread['collages'])) return spread;

    let spreadChanged = false;
    const collages = (spread['collages'] as Record<string, unknown>[]).map(collage => {
      if (!Array.isArray(collage['drawableObjects'])) return collage;
      const children = collage['drawableObjects'] as Record<string, unknown>[];
      const drawables = rewriteCaptionDrawables(children, filled, collageCaptionContext(children));
      if (drawables === children) return collage;
      spreadChanged = true;
      return { ...collage, drawableObjects: drawables };
    });

    if (!spreadChanged) return spread;
    changed = true;
    return { ...spread, collages };
  });

  return changed ? ({ ...modelRecord, spreads: next } as T) : model;
}

/**
 * Where the year goes in THIS collage — the year must print exactly once per stamp. A collage that
 * ships a real GRADUATION_YEAR box already gives it a home (the «ВЫПУСК» lines around it are decor
 * and stay words, exactly like a TITLE caption). A collage that stacks SUBTITLE2 and SUBTITLE3
 * «ВЫПУСК» with no year box is one stamp with one year line — the lower (SUBTITLE3) takes the year,
 * the word above it stays. The SUBTITLE3 gate is the BOX, not its current text: once the line is
 * filled (or authored) it stops reading «ВЫПУСК», and a sample-keyed gate would hand the year to
 * SUBTITLE2 on the very next load.
 */
interface CollageCaptionContext {
  readonly hasYearBox: boolean;
  readonly hasSubtitle3Box: boolean;
}

function collageCaptionContext(drawables: Record<string, unknown>[]): CollageCaptionContext {
  let hasYearBox = false;
  let hasSubtitle3Box = false;

  const walk = (nodes: Record<string, unknown>[]): void => {
    for (const obj of nodes) {
      if (obj['type'] === 'group' && Array.isArray(obj['drawableObjects'])) {
        walk(obj['drawableObjects'] as Record<string, unknown>[]);
        continue;
      }

      if (obj['type'] !== 'text') continue;
      if (obj['textType'] === TextTypeEnum.GRADUATION_YEAR) hasYearBox = true;
      if (obj['textType'] === VignetteTextTypeEnum.SUBTITLE3) hasSubtitle3Box = true;
    }
  };

  walk(drawables);

  return { hasYearBox, hasSubtitle3Box };
}

function isUntouchedSample(obj: Record<string, unknown>, sample: string): boolean {
  const current = (obj['text'] as Record<string, unknown> | undefined)?.['text'];

  return typeof current === 'string' && current.trim().toUpperCase() === sample;
}

function rewriteCaptionDrawables(
  drawables: Record<string, unknown>[],
  filled: Partial<Record<ProjectTextSettingKey, TextFieldSettings>>,
  ctx: CollageCaptionContext,
): Record<string, unknown>[] {
  let changed = false;

  const next = drawables.map(obj => {
    if (obj['type'] === 'group' && Array.isArray(obj['drawableObjects'])) {
      const children = rewriteCaptionDrawables(obj['drawableObjects'] as Record<string, unknown>[], filled, ctx);
      if (children === obj['drawableObjects']) return obj;
      changed = true;
      return { ...obj, drawableObjects: children };
    }

    if (obj['type'] !== 'text') return obj;

    const slot = VIGNETTE_CAPTION_SLOTS.get(obj['textType'] as string);
    if (!slot) return obj;

    const text = obj['text'] as Record<string, unknown> | undefined;
    const current = text?.['text'];
    // The year line (SUBTITLE3 with no year box) prints the year or nothing: its design «ВЫПУСК» repeats
    // the word above it. Blank stays its free slot, so a year typed later still lands there.
    const yearLine = obj['textType'] === VignetteTextTypeEnum.SUBTITLE3 && !ctx.hasYearBox;

    // Only the untouched design word is a free slot. Anything else on that box is somebody's own
    // words — the student's, the photographer's, or a previous fill — and outranks the album.
    if (!isUntouchedSample(obj, slot.sample) && !(yearLine && current === '')) return obj;

    if (slot.settingsKey === TextTypeEnum.GRADUATION_YEAR) {
      // A real year box on this collage already prints the year — every «ВЫПУСК» here is decor.
      if (ctx.hasYearBox) return obj;
      // Stacked SUBTITLE2+SUBTITLE3 pair: the lower line takes the year, the word above it stays.
      if (obj['textType'] === VignetteTextTypeEnum.SUBTITLE2 && ctx.hasSubtitle3Box) return obj;
    }

    const value = filled[slot.settingsKey]?.text || (yearLine ? '' : undefined);
    if (typeof value !== 'string' || value === current) return obj;

    changed = true;
    return { ...obj, text: { ...text, text: value } };
  });

  return changed ? next : drawables;
}
